/**
 * Created by shahar on 07-Nov-16.
 */
import {Inject, Injectable, OnDestroy} from "@angular/core";
import {DOCUMENT} from "@angular/common";
import {BrowserAttributes} from "../../browser/detect.browser";
import {BehaviorSubject, fromEvent, Observable, Subject} from "rxjs";
import {ThemeProxy} from "./theme.proxy";
import cssVars from "css-vars-ponyfill";
import {filter, flatMap, map, take} from "rxjs/operators";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {LogCat, LogManager} from "../../log";

export enum Themes{
	dark, light, dynamic
}

let supportedThemes=[
	Themes.dark, Themes.light, Themes.dynamic
];

interface PatientPortalTheme {
	commonThemeMap: { [key: string]: string };
	factoryColorGroupToColorMap: { [key: string]: string };
	customColorGroupToColorMap: { [key: string]: string };
}

@UntilDestroy()
@Injectable()
export class ThemeService implements OnDestroy{
	private themeSubject: BehaviorSubject<Themes> = new BehaviorSubject<Themes>(Themes.light);
	private cssVariablesLoadRequest:Subject<any>;
	private cssVariables: PatientPortalTheme = null;


	// we receive the document equivalent injected by the bootstrap
	constructor(@Inject(DOCUMENT) private doc : Document,
				private dynamicThemeVarisables:ThemeProxy,
				private bDetect:BrowserAttributes) {
		this.cssVariablesLoadRequest = new Subject();
		this.cssVariablesLoadRequest.asObservable().pipe(take(1), flatMap(() =>
			this.dynamicThemeVarisables.getPatientPortalDynamicThemeVariables()), untilDestroyed(this))
			.subscribe((patientPortalTheme: PatientPortalTheme) => {
				this.cssVariables = patientPortalTheme;
				patientPortalTheme.customColorGroupToColorMap ? this.setDynamicCustomTheme() : this.setDynamicFactoryTheme();

				fromEvent<StorageEvent>(window, "storage").pipe(
					filter((event: StorageEvent) => {
						return event.storageArea === sessionStorage && event.key === "dynamicTheme";
					}),
					map((event: StorageEvent) => {
						return event.newValue;
					}),
					untilDestroyed(this)
				).subscribe((themeType: "custom" | "factory") => {
					themeType === "custom" ? this.setDynamicCustomTheme() : this.setDynamicFactoryTheme();
				});
			});
	}

	public get currentTheme(){
		return this.themeSubject.getValue();
	}

	public get themeObservable(): Observable<Themes>{
		return this.themeSubject.asObservable();
	}

	public setToHostedContext(){
		if (this.bDetect.isNode()){
			// do nothing
		}else {
			this.doc.body.classList.add("hosted");
		}
	}

	public setTheme(t:Themes){
		if (t===this.currentTheme){
			LogManager.debug(LogCat.UI.Theme,"requested theme was already set");
		}else{
			if (this.bDetect.isNode()){
				LogManager.debug(LogCat.UI.Theme,"no theme setting required on node");
			}else {
				if (t===Themes.dynamic){
					this.cssVariablesLoadRequest.next(1);
				}
				this.doc.getElementById('kendo-theme').setAttribute('href',
					this.doc.getElementById('kendo-theme').getAttribute('href').replace(
						Themes[this.currentTheme], Themes[t]));

				// for backwards compatability, since until now the theme setting was a complete override no matter
				// what the current theme was, we will remove any possible theme before setting the new onw
				for (let currTheme in Themes) {
					if (typeof Themes[currTheme] === "number"){
						this.doc.body.classList.remove(this.getThemeStr(currTheme));
					}
				}

				this.doc.body.classList.add(this.getThemeStr(Themes[t]));

				this.themeSubject.next(t);
			}
		}
	}

	public rotateTheme(){
		// eslint-disable-next-line no-empty
		if (this.bDetect.isNode()){

		}else {
			let currTheme = this.doc.body.className;
			let currThemeIdx = supportedThemes.findIndex(x => this.getThemeStr(Themes[x]) === currTheme);
			let nextThemeIdx = (currThemeIdx + 1) % supportedThemes.length;
			this.setTheme(supportedThemes[nextThemeIdx]);
		}
	}

	public isThemeDynamicVars(){
		return this.currentTheme===Themes.dynamic;
	}

	public getThemeStr(t:string){
		return t+"-theme";
	}

	ngOnDestroy(): void {
	}

	public setDynamicFactoryTheme() {
		this.setDynamicTheme(this.cssVariables.factoryColorGroupToColorMap);
	}

	public setDynamicCustomTheme() {
		this.setDynamicTheme(this.cssVariables.customColorGroupToColorMap);
	}

	private setDynamicTheme(colorGroupToColorMap) {
		cssVars({
			variables: {
				...this.cssVariables.commonThemeMap,
				...(colorGroupToColorMap)
			},
			watch: true,
			include: "style"
		});
	}
}

